import {
  ActiveFilter,
  IconFilter,
  MagicTableFilterConfig,
  MagicTableProps
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { selectedCustomPropertyColumnsAtom } from 'modules/Customers/customers.atoms'
import { CustomersFilters, CustomersView } from 'modules/Customers/types'
import { useSetAtom } from 'jotai'
import { useMemo, useState } from 'react'

type Customer = CustomersView['model']

const CUSTOM_PROPERTY_PARAM_PREFIX = 'customProperties.'

/**
 * Fetches the set of customer custom property keys and returns:
 * - `allCustomPropertyKeys`: the raw list of known keys (used by
 *   `useCustomerCustomPropertyObjectPropertyColumns` to build the columns
 *   config).
 * - `customPropertyFilters`: a text filter per key, surfaced as top-level
 *   entries in the MagicTable filter menu. Spread into the consumer's filter
 *   array before threading it through `useLinkMagicTableWithSearchParams` so
 *   the URL parser recognises each `customProperties.<key>` param.
 *
 * Split from the columns hook because the filter list must be available before
 * `useLinkMagicTableWithSearchParams` runs, but the columns config depends on
 * the resulting `activeFilters` — so the two pieces sit on either side of that
 * call.
 *
 * A saved view's custom property filters also get a filter config, before the
 * keys load and even when no customer has the key any more. Without a config,
 * the list query drops the filter.
 */
export const useCustomerCustomPropertyFilters = (
  forcedActiveFilters: ActiveFilter[]
) => {
  const customPropertyKeysQuery = useQuery(
    dashboard20240730Client.getCustomerCustomPropertyKeys
  )
  const allCustomPropertyKeys = useMemo(
    () => customPropertyKeysQuery.data?.items ?? [],
    [customPropertyKeysQuery.data?.items]
  )

  const customPropertyFilters = useMemo<
    MagicTableFilterConfig<CustomersFilters>[]
  >(() => {
    const forcedKeys = forcedActiveFilters
      .filter(filter =>
        filter.paramName.startsWith(CUSTOM_PROPERTY_PARAM_PREFIX)
      )
      .map(filter =>
        filter.paramName.slice(CUSTOM_PROPERTY_PARAM_PREFIX.length)
      )

    return Array.from(new Set([...allCustomPropertyKeys, ...forcedKeys])).map(
      key => ({
        type: 'text' as const,
        paramName: `${CUSTOM_PROPERTY_PARAM_PREFIX}${key}`,
        label: key,
        icon: IconFilter
      })
    )
  }, [allCustomPropertyKeys, forcedActiveFilters])

  return { allCustomPropertyKeys, customPropertyFilters }
}

type ObjectPropertyColumnsResult = NonNullable<
  MagicTableProps<CustomersView>['objectPropertyColumns']
>

/**
 * Builds the `objectPropertyColumns` config that drives the custom property
 * columns on the customers MagicTable:
 * - Pulls values off each customer's `customProperties` map.
 * - Forces a column to render for any custom property with an active filter so
 *   its values are immediately visible.
 * - Maps each key to a sortable backend property (`customProperties.<key>`).
 * - Mirrors the selected columns into the jotai atom consumed by the CSV
 *   export hook.
 *
 * Pair with `useCustomerCustomPropertyFilters`. Pass `activeFilters` after it
 * has been derived from the filter array (which must include the
 * `customPropertyFilters` from the filters hook).
 */
export const useCustomerCustomPropertyObjectPropertyColumns = ({
  allCustomPropertyKeys,
  activeFilters
}: {
  allCustomPropertyKeys: string[]
  activeFilters: ActiveFilter[]
}): ObjectPropertyColumnsResult => {
  const [customPropertyKeysSearch, setCustomPropertyKeysSearch] = useState('')
  const filteredCustomPropertyKeys = useMemo(
    () =>
      allCustomPropertyKeys.filter(key =>
        key.toLowerCase().includes(customPropertyKeysSearch.toLowerCase())
      ),
    [allCustomPropertyKeys, customPropertyKeysSearch]
  )

  const setSelectedCustomPropertyColumns = useSetAtom(
    selectedCustomPropertyColumnsAtom
  )

  // The custom property keys that currently have an active filter value, so
  // each filtered key is always shown as a column.
  const activeCustomPropertyFilters = useMemo(
    () =>
      activeFilters.reduce<Record<string, string>>((acc, filter) => {
        if (filter.type !== 'text' || !filter.value) return acc
        if (!filter.paramName.startsWith(CUSTOM_PROPERTY_PARAM_PREFIX))
          return acc
        const key = filter.paramName.slice(CUSTOM_PROPERTY_PARAM_PREFIX.length)
        if (!key) return acc
        return { ...acc, [key]: filter.value }
      }, {}),
    [activeFilters]
  )

  const filteredCustomPropertyKeysForColumns = useMemo(
    () => Object.keys(activeCustomPropertyFilters),
    [activeCustomPropertyFilters]
  )

  return useMemo<ObjectPropertyColumnsResult>(
    () => ({
      allEventProperties: filteredCustomPropertyKeys,
      getObject: (model: Customer) => model.customProperties ?? {},
      searchFunc: (query: string) => {
        setCustomPropertyKeysSearch(query)
        return allCustomPropertyKeys.map(key => ({
          label: key,
          value: key
        }))
      },
      menuLabel: 'Custom properties',
      searchPlaceholder: 'Search custom properties',
      valueAsPill: false,
      onSelectedChange: setSelectedCustomPropertyColumns,
      requiredKeys: filteredCustomPropertyKeysForColumns,
      // Custom property keys are surfaced as top-level entries in the add
      // column / view settings menus rather than under a sub-menu.
      inlineInAddColumnMenu: true,
      // Server accepts `customProperties.<key>` as a sort key; map each
      // custom-property column to its corresponding sort property so the
      // column headers offer Sort ascending / descending alongside the static
      // columns.
      sortPropertyForKey: (key: string) =>
        `${CUSTOM_PROPERTY_PARAM_PREFIX}${key}`
    }),
    [
      allCustomPropertyKeys,
      filteredCustomPropertyKeys,
      filteredCustomPropertyKeysForColumns,
      setSelectedCustomPropertyColumns
    ]
  )
}
